import { LocalizationProvider } from '@localization/LocalizationProvider';
import styles from './FreeFieldsComponent.module.scss';

import { CdfGrid } from '@commonComponents/data/grid/CdfGrid/CdfGrid.tsx';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN } from '@ag-grid-community/locale';
import { GridApi } from 'ag-grid-enterprise';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { RowSelectionMode } from '@models/enums/RowSelectionMode';
import { PermissionProvider } from '@auth/PermissionProvider.tsx';
import { useInjectReducers } from '@hooks/useInjectReducers.ts';
import { ApiCallType } from '@api/ApiCalls.ts';
import { useCustomFieldsPermissions } from '@hooks/permissions/useCustomFieldsPermissions';
import { FreeFieldBase } from '@models/DomainModels/Master/free-field-base';
import { gridColumnDefinitions } from './grid-column-definitions';
import { DynamicStore } from '@state/interfaces/idynamic-store';
import classNames from 'classnames';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '@commonComponents/core/ErrorFallback';
import { logger } from '@utils/logging-utils';
import type { EntityDialogCloseResult } from '@commonComponents/dialogs/EntityDetailDialog/useEntityDetail';

import { ThemeProvider } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import brzTheme from '@brz/brz-global-mui-theme';
import { Typography } from '@mui/material';

import { FreeFieldDetailDialogWrapper } from './FreeFieldDetailDialog';

interface FreeFieldsComponentProps {
  readonly organizationId: string;
  readonly store: DynamicStore | undefined;
}

const extendedAG_GRID_LOCALE_DE = {
  ...AG_GRID_LOCALE_DE,
  ariaHeaderSelection: ''
};

/**
 * Component for managing free fields
 * Displays a grid of free fields with CRUD operations
 */
function FreeFieldsComponent({ organizationId, store }: Readonly<FreeFieldsComponentProps>) {
  const { writePermission } = useCustomFieldsPermissions(organizationId);

  const { t, i18n } = useTranslation(['freeFields', 'common', 'enums']);

  const [localeText, setLocaleText] = useState(AG_GRID_LOCALE_EN);
  const [gridKey, setGridKey] = useState(0);
  const gridApiRef = useRef<GridApi | null>(null);
  const gridRef = useRef<{
    refresh: () => void;
    addCreatedItem: (item: FreeFieldBase) => void;
    updateModifiedItem: (item: FreeFieldBase) => void;
  }>(null);
  const selectedIdsRef = useRef<string[]>([]);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedFreeFieldData, setSelectedFreeFieldData] = useState<FreeFieldBase | undefined>(undefined);

  /**
   * Handle language change and update the grid locale
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLocaleText = lng === 'de' ? extendedAG_GRID_LOCALE_DE : AG_GRID_LOCALE_EN;
      setLocaleText(newLocaleText);
      setGridKey(prevKey => prevKey + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Grid ready callback to store the grid API reference
   */
  const onGridReady = useCallback((params: { api: GridApi }) => {
    gridApiRef.current = params.api;
  }, []);

  /**
   * Handle clicking on a free field detail
   * @param data The free field data
   */
  const handleDetailClick = useCallback((data: FreeFieldBase) => {
    if (!data?.id) {
      logger.warn('FreeFieldsComponent', 'Detail click with invalid data', data);
      return;
    }

    selectedIdsRef.current = [data.id];
    setSelectedFreeFieldData(data);
    setDetailDialogOpen(true);
  }, []);

  const handleDetailClose = useCallback((result: EntityDialogCloseResult<FreeFieldBase>) => {
    setDetailDialogOpen(false);

    if (result.action === 'created' && result.data) {
      gridRef.current?.addCreatedItem(result.data);
    } else if (result.action === 'updated' && result.data) {
      gridRef.current?.updateModifiedItem(result.data);
    }

    setSelectedFreeFieldData(undefined);
  }, []);

  /**
   * Memoized column definitions to prevent unnecessary re-renders
   */
  const columnDefs = useCallback(() => gridColumnDefinitions({ t }), [t]);

  /**
   * Handle clicking the add button to create a new free field
   */
  const handleAddButtonClick = useCallback(() => {
    setSelectedFreeFieldData(undefined);
    setDetailDialogOpen(true);
  }, []);

  return (
    <Grid className={styles.freeFieldsComponent}>
      <Typography variant="h4" component="h1">
        {t('title')}
      </Typography>
      <Grid className={styles.content}>
        <Grid className={classNames(styles.grid)}>
          <CdfGrid<FreeFieldBase>
            ref={gridRef}
            apiCallType={ApiCallType.FetchFreeFields}
            key={gridKey}
            columnDefs={columnDefs()}
            gridOptions={{ onGridReady, localeText }}
            rowSelection={RowSelectionMode.Multiple}
            showAddButton={writePermission}
            showDeleteButton={writePermission}
            showDeleteButtonInGrid={writePermission}
            onAddClick={handleAddButtonClick}
            addButtonText={t('addButtonText')}
            onDoubleClick={event => event.data && handleDetailClick(event.data)}
          />
        </Grid>
      </Grid>
      {detailDialogOpen && (
        <FreeFieldDetailDialogWrapper
          entityData={selectedFreeFieldData}
          open={detailDialogOpen}
          onClose={handleDetailClose}
          organizationId={organizationId}
          store={store}
        />
      )}
    </Grid>
  );
}

/**
 * Wrapper component for FreeFieldsComponent
 * Provides necessary context providers and error boundaries
 */
export function FreeFieldsComponentWrapper({ organizationId, store }: Readonly<FreeFieldsComponentProps>) {
  const reducerInjected = useInjectReducers(store, organizationId);
  if (!reducerInjected) {
    return null;
  }

  return (
    <ThemeProvider theme={brzTheme}>
      <LocalizationProvider>
        {/* Type assertion used to handle DynamicStore compatibility with Redux Provider */}
        {store && (
          <Provider store={store}>
            <PermissionProvider>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <FreeFieldsComponent organizationId={organizationId} store={store} />
              </ErrorBoundary>
            </PermissionProvider>
          </Provider>
        )}
        {!store && <div>Store is not available. Please ensure the Redux store is properly initialized.</div>}
      </LocalizationProvider>
    </ThemeProvider>
  );
}
